/**
 * Currency and VAT formatting utilities for backoffice UI
 */

/**
 * Format currency amount with symbol
 * @param amount - Amount to format
 * @param currency - Currency code (default: EUR)
 * @returns Formatted currency string (e.g., "€8.90" or "-")
 */
export function formatCurrency(amount: number | null | undefined, currency: string = 'EUR'): string {
  if (amount === null || amount === undefined) return '-';
  const symbol = currency === 'EUR' ? '€' : currency;
  return `${symbol}${amount.toFixed(2)}`;
}

/**
 * Format VAT rate as percentage
 * @param rate - VAT rate as decimal (e.g., 0.19 for 19%)
 * @returns Formatted percentage string (e.g., "19%" or "25.5%")
 */
export function formatVATRate(rate: number | null | undefined): string {
  if (rate === null || rate === undefined) return '-';
  // Handle fractional percentages correctly (e.g., 25.5%)
  const percentage = Math.round(rate * 1000) / 10;
  return `${percentage}%`;
}

/**
 * Format country code with optional flag emoji
 * @param code - ISO 3166-1 alpha-2 country code (e.g., "DE", "FR")
 * @param withFlag - Whether to include flag emoji
 * @returns Formatted country code (e.g., "DE 🇩🇪" or "-")
 */
export function formatCountryCode(code: string | null | undefined, withFlag: boolean = false): string {
  if (!code) return '-';

  // Handle special "Other Non-EU" code
  if (code === 'XX') {
    return withFlag ? 'Other 🌍' : 'Other';
  }

  if (!withFlag) return code;

  try {
    // Convert country code to flag emoji using regional indicator symbols
    const codePoints = code
      .toUpperCase()
      .split('')
      .map(char => 127397 + char.charCodeAt(0));
    const flag = String.fromCodePoint(...codePoints);

    return `${code} ${flag}`;
  } catch (error) {
    // Fallback if flag emoji fails to render
    return code;
  }
}

/** Preserve the currency's exponent, including zero-decimal currencies. */
export function formatMinorAmount(amount: number, exponent?: number | null): string {
  const decimals = exponent ?? 2;
  return (amount / 10 ** decimals).toFixed(decimals);
}
